import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import RefreshIcon from '@mui/icons-material/Refresh';

const DataLakeOverview = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  
  const API_URL = `${process.env.REACT_APP_API_URL}/system-info/datalake`;
  const API_KEY = process.env.REACT_APP_API_KEY;

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching Data Lake status:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 300000);
    return () => clearInterval(interval);
  }, []);

  const handleForceUpdate = async () => {
    try {
      setTriggering(true);
      await axios.post(`${API_URL}/trigger`, {}, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      setTimeout(() => {
        fetchStatus();
        setTriggering(false);
      }, 2000);
    } catch (err) {
      console.error('Error triggering Data Lake update:', err);
      setError(err);
      setTriggering(false);
    }
  };

  if (loading && !status) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const etlJob = status?.etlJob || {};
  const crawler = status?.crawler || {};
  const isRunning = etlJob.status === 'RUNNING' || crawler.state === 'RUNNING';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <StorageIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h4">Data Lake Overview</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to fetch Data Lake status. Please check your connection.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Pipeline Controls & Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Pipeline Status</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="text.secondary">ETL Job</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: etlJob.status === 'SUCCEEDED' ? 'success.main' : etlJob.status === 'RUNNING' ? 'info.main' : 'error.main' }}>
                    {etlJob.status || 'UNKNOWN'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">Crawler</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: crawler.state === 'READY' ? 'success.main' : 'info.main' }}>
                    {crawler.state || 'UNKNOWN'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography color="text.secondary">Last ETL Start</Typography>
              <Typography variant="body1">
                {etlJob.startedOn ? new Date(etlJob.startedOn).toLocaleString() : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography color="text.secondary">Last ETL Completion</Typography>
              <Typography variant="body1">
                {etlJob.completedOn ? new Date(etlJob.completedOn).toLocaleString() : 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography color="text.secondary">Last Crawler Completion</Typography>
              <Typography variant="body1">
                {crawler.lastStartedOn ? new Date(crawler.lastStartedOn).toLocaleString() : 'N/A'}
              </Typography>
            </Box>

            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              size="large"
              onClick={handleForceUpdate} 
              disabled={isRunning || triggering}
              startIcon={(isRunning || triggering) ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            >
              {isRunning ? 'Pipeline Running...' : 'Force Data Lake Update'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Triggers the Glue ETL Job, which cascades into the Crawler on success.
            </Typography>
          </Paper>
        </Grid>

        {/* System Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>System Information</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography color="text.secondary" gutterBottom>Architecture</Typography>
              <Typography variant="body2" paragraph>
                The Titicaca Data Lake extracts operational data from DynamoDB into optimized Parquet files in S3. 
                It utilizes the Central Scoring Framework to compute visibility and tile scores across multiple mathematical strategies.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography color="text.secondary" gutterBottom>Key Views</Typography>
              <ul>
                <li><Typography variant="body2"><strong>mart_positioning_inputs</strong>: Raw observations joined with dimensions.</Typography></li>
                <li><Typography variant="body2"><strong>mart_tile_scores</strong>: Contains all raw component scores.</Typography></li>
                <li><Typography variant="body2"><strong>mart_visibility_count_daily</strong>: Daily visibility rollups.</Typography></li>
              </ul>
            </Box>
            
            <Box>
              <Typography color="text.secondary" gutterBottom>AWS Resources</Typography>
              <Typography variant="body2"><strong>S3 Bucket:</strong> titicaca-data-lake</Typography>
              <Typography variant="body2"><strong>ETL Job:</strong> titicaca-dynamo-to-parquet-etl</Typography>
              <Typography variant="body2"><strong>Crawler:</strong> titicaca-data-lake-crawler</Typography>
              <Typography variant="body2"><strong>Database:</strong> titicaca_data_lake</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DataLakeOverview;
